import { motion } from "framer-motion";
import { ArrowUpRight, Mail } from "lucide-react";
import { EMAIL } from "@/lib/constants";
import { useBooking } from "@/lib/bookingStore";

const EASE = [0.16, 1, 0.3, 1];

export const Contact = () => {
    const openBooking = useBooking((s) => s.openBooking);
    return (
    <section
        id="contact"
        data-testid="contact-section"
        className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32"
    >
        <p className="font-mono-x mb-10 text-xs uppercase tracking-[0.25em] text-zinc-500">
            [ Contact ]
        </p>

        <h2 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-[13vw] lg:text-[9vw]">
            {[
                { text: "HAVE AN IDEA?", outline: false },
                { text: "MAKE IT RARE.", outline: true, accent: "RARE." },
            ].map((line, i) => (
                <motion.span
                    key={line.text}
                    className="block overflow-hidden pb-1"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-80px" }}
                >
                    <motion.span
                        className={`block ${line.outline ? "text-outline" : ""}`}
                        variants={{ hidden: { y: "115%" }, visible: { y: "0%" } }}
                        transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
                    >
                        {line.accent ? (
                            <>
                                {line.text.replace(line.accent, "")}
                                <span
                                    data-testid="contact-rare-accent"
                                    className="font-logo font-extrabold text-white [-webkit-text-stroke:0px]"
                                >
                                    {line.accent}
                                </span>
                            </>
                        ) : (
                            line.text
                        )}
                    </motion.span>
                </motion.span>
            ))}
        </h2>

        <div className="mt-16 grid gap-16 md:mt-24 md:grid-cols-12">
            <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: EASE }}
                className="md:col-span-7"
            >
                <button
                    data-testid="contact-book-call-button"
                    onClick={openBooking}
                    className="group inline-flex items-center gap-4 rounded-full bg-white px-10 py-6 font-display text-xl font-bold text-[#050505] transition-transform duration-300 hover:scale-[1.04] md:px-14 md:py-8 md:text-3xl"
                >
                    Book a Call
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-[#050505] text-white transition-transform duration-300 group-hover:rotate-45 md:h-12 md:w-12">
                        <ArrowUpRight className="h-4 w-4 md:h-6 md:w-6" />
                    </span>
                </button>
                <p className="font-mono-x mt-6 text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    30 minutes · Zero pitch · We map your idea to a launch plan
                </p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                className="flex flex-col justify-end gap-10 md:col-span-5"
            >
                <a
                    data-testid="contact-email-link"
                    href={`mailto:${EMAIL}`}
                    className="group flex items-center gap-4 text-white"
                >
                    <span className="grid h-12 w-12 place-items-center rounded-full border border-white/15 transition-colors duration-300 group-hover:border-white/50 group-hover:bg-white/5">
                        <Mail className="h-5 w-5" />
                    </span>
                    <span className="font-display text-xl font-bold tracking-tight md:text-2xl">
                        {EMAIL}
                    </span>
                </a>
            </motion.div>
        </div>
    </section>
    );
};
